import { useTranslation } from 'react-i18next';
import {
  Checkbox,
  Chip,
  DateField,
  Notice,
  SelectField,
  Segmented,
  TextField,
} from '@sauda/ui/kit';
import {
  BROKERAGE_PAYERS,
  FURNISHING_ITEMS,
  OWNER_SIGNING,
  PARKING_TYPES,
  STAY_RULES,
  type PropertyFacts,
} from '@sauda/shared';
import { draftCompleteness, mapTextProblem, type PropertyDraft } from '../../lib/property-form.js';

interface Props {
  draft: PropertyDraft;
  onChange: (patch: Partial<PropertyDraft>) => void;
}

type Facts = PropertyFacts;
const num = (value: string): number | null => {
  const digits = value.replace(/[^\d.]/g, '');
  return digits === '' || Number.isNaN(Number(digits)) ? null : Number(digits);
};
const show = (value: number | null | undefined): string => (value == null ? '' : String(value));

/** A choice that can also be left unsaid: a blank is shown as "Ask the broker", never a made up answer. */
function Choice({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string | null | undefined;
  options: { value: string; label: string }[];
  onChange: (value: string | null) => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="choice-field">
      <span className="k-t-label-lg">{label}</span>
      <Segmented
        label={label}
        value={value ?? ''}
        onChange={(v) => onChange(v === '' ? null : v)}
        options={[{ value: '', label: t('listing.unsaid') }, ...options]}
      />
    </div>
  );
}

/**
 * What the broker tells about the flat: brokerage, furnishing, who can stay, terms, building,
 * location and the owner. Every one is optional except the brokerage before sharing; the meter
 * says how complete the listing is, and a sparse listing is allowed.
 */
export function ListingFactsFields({ draft, onChange }: Props) {
  const { t } = useTranslation();
  const facts = draft.facts;
  const set = (patch: Partial<Facts>) => onChange({ facts: { ...facts, ...patch } });
  const terms = facts.terms ?? {};
  const building = facts.building ?? {};
  const owner = facts.owner ?? {};
  const stay = (facts.stay ?? {}) as Record<string, string | undefined>;
  const brokerage = facts.brokerage ?? null;
  const meter = draftCompleteness(draft);
  const yesNo = [
    { value: 'yes', label: t('listing.yes') },
    { value: 'no', label: t('listing.no') },
  ];
  const items = facts.furnishingItems ?? [];
  const toggleItem = (item: (typeof FURNISHING_ITEMS)[number]) =>
    set({
      furnishingItems: items.includes(item) ? items.filter((i) => i !== item) : [...items, item],
    });
  return (
    <div className="form-stack">
      <p className="k-t-label" role="status" data-testid="completeness">
        {t('listing.meter', { filled: meter.filled, total: meter.total })}
      </p>

      <h2 className="k-t-title-sm">{t('listing.brokerageTitle')}</h2>
      <p className="k-t-body-sm k-muted">{t('listing.brokerageHelp')}</p>
      <span className="k-t-label-lg">{t('listing.brokerageKind')}</span>
      <Segmented
        label={t('listing.brokerageKind')}
        value={brokerage?.kind ?? ''}
        onChange={(v) =>
          set({
            brokerage:
              v === ''
                ? null
                : { kind: v as 'amount' | 'months', payer: brokerage?.payer ?? 'tenant' },
          })
        }
        options={[
          { value: '', label: t('listing.unsaid') },
          { value: 'amount', label: t('listing.inRupees') },
          { value: 'months', label: t('listing.inMonths') },
        ]}
      />
      {brokerage === null ? null : (
        <>
          <TextField
            label={
              brokerage.kind === 'amount'
                ? t('listing.brokerageAmount')
                : t('listing.brokerageMonths')
            }
            prefix={brokerage.kind === 'amount' ? '₹' : undefined}
            inputMode="decimal"
            value={draft.brokerageText}
            onChange={(e) => onChange({ brokerageText: e.target.value })}
          />
          <span className="k-t-label-lg">{t('listing.whoPays')}</span>
          <Segmented
            label={t('listing.whoPays')}
            value={brokerage.payer}
            onChange={(v) =>
              set({ brokerage: { ...brokerage, payer: v as (typeof BROKERAGE_PAYERS)[number] } })
            }
            options={BROKERAGE_PAYERS.map((p) => ({ value: p, label: t(`listing.payer_${p}`) }))}
          />
        </>
      )}

      <h2 className="k-t-title-sm">{t('listing.furnishingTitle')}</h2>
      <div className="chips-row" role="group" aria-label={t('listing.furnishingItems')}>
        {FURNISHING_ITEMS.map((item) => (
          <Chip key={item} selected={items.includes(item)} onClick={() => toggleItem(item)}>
            {t(`fact.item_${item}`)}
          </Chip>
        ))}
      </div>

      <h2 className="k-t-title-sm">{t('listing.stayTitle')}</h2>
      {STAY_RULES.map((rule) => (
        <Choice
          key={rule}
          label={t(`fact.stay_${rule}`)}
          value={stay[rule]}
          options={[...yesNo, { value: 'ask', label: t('listing.ask') }]}
          onChange={(v) => {
            const next = { ...stay };
            if (v === null) delete next[rule];
            else next[rule] = v;
            set({ stay: next as Facts['stay'] });
          }}
        />
      ))}

      <h2 className="k-t-title-sm">{t('listing.termsTitle')}</h2>
      <p className="k-t-body-sm k-muted">{t('listing.termsHelp')}</p>
      <div className="form-pair">
        <TextField
          label={t('listing.lockIn')}
          inputMode="numeric"
          value={show(terms.lockInMonths)}
          onChange={(e) => set({ terms: { ...terms, lockInMonths: num(e.target.value) } })}
        />
        <TextField
          label={t('listing.notice')}
          inputMode="numeric"
          value={show(terms.noticeDays)}
          onChange={(e) => set({ terms: { ...terms, noticeDays: num(e.target.value) } })}
        />
      </div>
      <div className="form-pair">
        <TextField
          label={t('listing.increase')}
          inputMode="decimal"
          value={show(terms.rentIncreasePct)}
          onChange={(e) => set({ terms: { ...terms, rentIncreasePct: num(e.target.value) } })}
        />
        <TextField
          label={t('listing.refund')}
          inputMode="numeric"
          value={show(terms.depositRefundDays)}
          onChange={(e) => set({ terms: { ...terms, depositRefundDays: num(e.target.value) } })}
        />
      </div>
      <h2 className="k-t-title-sm">{t('listing.availabilityTitle')}</h2>
      <DateField
        label={t('listing.availableFrom')}
        value={draft.availableFrom}
        onChange={(v) => onChange({ availableFrom: v })}
      />

      <h2 className="k-t-title-sm">{t('listing.buildingTitle')}</h2>
      <TextField
        label={t('listing.totalFloors')}
        inputMode="numeric"
        value={show(building.totalFloors)}
        onChange={(e) => set({ building: { ...building, totalFloors: num(e.target.value) } })}
      />
      <Choice
        label={t('fact.lift')}
        value={building.lift}
        options={yesNo}
        onChange={(v) => set({ building: { ...building, lift: v as 'yes' | 'no' | null } })}
      />
      <div className="form-pair">
        <SelectField
          label={t('fact.parking')}
          value={building.parkingType ?? ''}
          onChange={(e) =>
            set({
              building: {
                ...building,
                parkingType: (e.target.value || null) as (typeof PARKING_TYPES)[number] | null,
              },
            })
          }
          options={[
            { value: '', label: t('listing.unsaid') },
            ...PARKING_TYPES.map((p) => ({ value: p, label: t(`fact.parking_${p}`) })),
          ]}
        />
        <TextField
          label={t('listing.parkingCount')}
          inputMode="numeric"
          value={show(building.parkingCount)}
          onChange={(e) => set({ building: { ...building, parkingCount: num(e.target.value) } })}
        />
      </div>
      <TextField
        label={t('fact.water')}
        supporting={t('listing.waterHint')}
        value={building.waterSupply ?? ''}
        onChange={(e) => set({ building: { ...building, waterSupply: e.target.value || null } })}
      />
      <TextField
        label={t('fact.powerBackup')}
        supporting={t('listing.powerHint')}
        value={building.powerBackup ?? ''}
        onChange={(e) => set({ building: { ...building, powerBackup: e.target.value || null } })}
      />
      <Choice
        label={t('fact.gated')}
        value={building.gated}
        options={yesNo}
        onChange={(v) => set({ building: { ...building, gated: v as 'yes' | 'no' | null } })}
      />
      <div className="form-pair">
        <TextField
          label={t('fact.age')}
          inputMode="numeric"
          value={show(building.ageYears)}
          onChange={(e) => set({ building: { ...building, ageYears: num(e.target.value) } })}
        />
        <TextField
          label={t('fact.facing')}
          supporting={t('listing.facingHint')}
          value={building.facing ?? ''}
          onChange={(e) => set({ building: { ...building, facing: e.target.value || null } })}
        />
      </div>
      <TextField
        label={t('fact.societyRules')}
        maxLength={300}
        value={building.societyRules ?? ''}
        onChange={(e) => set({ building: { ...building, societyRules: e.target.value || null } })}
      />
      <TextField
        label={t('fact.visitorTiming')}
        maxLength={120}
        value={building.visitorTiming ?? ''}
        onChange={(e) => set({ building: { ...building, visitorTiming: e.target.value || null } })}
      />
      <TextField
        label={t('fact.nearestMainRoad')}
        maxLength={120}
        value={building.nearestMainRoad ?? ''}
        onChange={(e) =>
          set({ building: { ...building, nearestMainRoad: e.target.value || null } })
        }
      />

      <h2 className="k-t-title-sm">{t('listing.locationTitle')}</h2>
      <TextField
        label={t('listing.mapLink')}
        value={draft.mapText}
        error={mapTextProblem(draft) ? t('listing.mapProblem') : undefined}
        supporting={t('listing.mapHelp')}
        onChange={(e) => onChange({ mapText: e.target.value })}
      />
      <TextField
        label={t('listing.locationNote')}
        maxLength={120}
        value={facts.location?.note ?? ''}
        onChange={(e) => set({ location: { ...facts.location, note: e.target.value || null } })}
      />

      <h2 className="k-t-title-sm">{t('listing.ownerTitle')}</h2>
      <p className="k-t-body-sm k-muted">{t('listing.ownerHelp')}</p>
      <Choice
        label={t('listing.ownerLives')}
        value={owner.livesInSociety}
        options={yesNo}
        onChange={(v) => set({ owner: { ...owner, livesInSociety: v as 'yes' | 'no' | null } })}
      />
      <Choice
        label={t('listing.ownerSigns')}
        value={owner.signing}
        options={OWNER_SIGNING.map((o) => ({ value: o, label: t(`listing.signing_${o}`) }))}
        onChange={(v) =>
          set({ owner: { ...owner, signing: v as (typeof OWNER_SIGNING)[number] | null } })
        }
      />
      <h2 className="k-t-title-sm">{t('listing.photosTitle')}</h2>
      <Checkbox
        label={t('listing.representative')}
        checked={facts.photos?.representative === true}
        onChange={(e) => set({ photos: { ...facts.photos, representative: e.target.checked } })}
      />
      {facts.photos?.representative === true ? (
        <Notice tone="info">{t('listing.representativeNote')}</Notice>
      ) : null}
    </div>
  );
}
